/* ──────────────────────────────────────────────────────────────────────────
 * Mobility Intelligence — §11.
 *
 * The brief's claim is the one worth engineering against:
 *
 *   "Crowd and transport cannot be separate systems...
 *    A transport disruption upstream can become crowd pressure downstream."
 *
 * So this models the chain as a single connected system —
 *
 *   Railway arrival → Bus transfer → Parking → Pedestrian corridor
 *                   → Ghat → Exit → Return transport
 *
 * — and does the thing that makes it intelligence rather than a diagram: it
 * propagates. A hold at the bus terminal is not reported as "bus terminal
 * degraded". It is reported as the crowd pressure it becomes at the ghat,
 * and the number of minutes before it gets there.
 *
 * ── WHY IT IS A CHAIN AND NOT SIX GAUGES ──
 * Each stage has a throughput capacity. Flow through the chain is limited by
 * the SMALLEST capacity upstream of a point, not by the local one — which is
 * why a station that is coping can still be the reason a ghat is not. The
 * binding constraint is computed, named, and attributed to its agency,
 * because the department that must act is rarely the one reporting the
 * symptom.
 *
 * Everything is SIMULATED. Stage capacities are planning assumptions.
 * ────────────────────────────────────────────────────────────────────────── */

import { TRUST } from "../data/provenance";
import { ASSEMBLY_AREA_M2 } from "./crowdSafety";
import { buildTwin } from "../../twin/twin";
import { PARKING_BAYS, VEHICLE_OCCUPANCY, PARKING_DWELL_HOURS } from "../../twin/entities";

/* Walking and transfer times between stages, in minutes. These are what
 * make propagation a forecast rather than an alarm: a disruption at the
 * station reaches the ghat after the sum of the lags between them. */
export const STAGE_LAG_MIN = {
  rail_to_bus: 12,
  bus_to_parking: 8,
  parking_to_corridor: 6,
  corridor_to_ghat: 9,
  ghat_to_exit: 4,
  exit_to_return: 11,
};

const STAGE_ORDER = [
  "rail", "bus", "parking", "corridor", "ghat", "exit", "return",
];

const STAGE_META = {
  rail:     { label: { en: "Railway arrival",   mr: "रेल्वे आगमन" },      agency: "TRANSPORT" },
  bus:      { label: { en: "Bus transfer",      mr: "बस हस्तांतरण" },     agency: "TRANSPORT" },
  parking:  { label: { en: "Parking",           mr: "वाहनतळ" },          agency: "TRANSPORT" },
  corridor: { label: { en: "Pedestrian corridor", mr: "पादचारी मार्गिका" }, agency: "POLICE" },
  ghat:     { label: { en: "Ghat",              mr: "घाट" },             agency: "NMC" },
  exit:     { label: { en: "Exit",              mr: "निर्गमन" },          agency: "POLICE" },
  return:   { label: { en: "Return transport",  mr: "परतीचा प्रवास" },    agency: "TRANSPORT" },
};

/* Cumulative minutes from the head of the chain to each stage. */
function cumulativeLag() {
  const lags = [0];
  const keys = [
    "rail_to_bus", "bus_to_parking", "parking_to_corridor",
    "corridor_to_ghat", "ghat_to_exit", "exit_to_return",
  ];
  keys.forEach((k, i) => lags.push(lags[i] + STAGE_LAG_MIN[k]));
  return Object.fromEntries(STAGE_ORDER.map((s, i) => [s, lags[i]]));
}

export const CUMULATIVE_LAG_MIN = cumulativeLag();

/* Nashik Road station concourse + platform throughput. A planning figure
 * for a major station running at mela staffing, not a surveyed one. */
export const STATION_THROUGHPUT_PER_MIN = 900;

/* Ghat processing. Usable bathing area per pilgrim, and how long each
 * stays. Both are planning assumptions and both move the answer a lot. */
export const GHAT_AREA_PER_PERSON_M2 = 2;
export const GHAT_DWELL_MIN = 20;

/* Mode share of arrivals across the three parallel ingress channels.
 * Most pilgrims at a Kumbh arrive by rail or bus; private vehicle is the
 * smallest share, which is exactly why treating parking as a stage every
 * arrival passes through is so distorting. Shares must sum to 1. */
export const INGRESS_CHANNELS = {
  rail: 0.34,
  bus: 0.44,
  parking: 0.22,
};

/* ── Stage capacities ──────────────────────────────────────────────────
 * Read from the twin wherever the twin knows, so the chain and the map
 * cannot disagree about how wide a corridor is. */
function stageCapacities(twin, liveState) {
  const sum = (arr, f) => arr.reduce((a, x) => a + f(x), 0);

  const busTerminals = twin.byType("busTerminal");
  const parking = twin.byType("parking");
  const corridors = twin.byType("corridor");

  /* Rail CAPACITY is the station's processing throughput — platforms,
   * concourse, ticket hall — not how many people the timetable happens to
   * bring. Conflating the two (which this did until it was caught) makes
   * the daily average arrival rate look like a hard ceiling, pins the
   * binding constraint on rail at every hour of the day, and reports a
   * theatre-wide chain throughput of about twelve people per minute.
   *
   * Arrivals are DEMAND and enter the chain separately below. */
  const railPerMin = STATION_THROUGHPUT_PER_MIN;
  const busPerMin = Math.round(sum(busTerminals, (b) => b.dispatchPerHour ?? 0) * 55 / 60);
  /* A parking area's constraint is turnover, not standing capacity: bays
   * times occupants, divided by how long each vehicle stays. Derived from
   * the twin's bay counts so the figure traces to an asset a department
   * could go and count, rather than a flat per-lot guess. */
  const parkingPerMin = Math.round(
    parking.reduce(
      (a, lot) => a + ((PARKING_BAYS[lot.id] ?? 0) * VEHICLE_OCCUPANCY) / PARKING_DWELL_HOURS,
      0,
    ) / 60,
  );
  const corridorPerMin = sum(corridors, (c) => c.capacityPerMin ?? 0);
  /* Ghat CAPACITY is how many pilgrims the bathing areas can process per
   * minute: usable area at ~2 m² per person, turned over every 20 minutes.
   * Deriving it from current inflow (as this did) is circular — it makes
   * the ghat stage always exactly meet demand and therefore never appear
   * as a constraint, which is the one thing it most often is. */
  const ghatPerMin = Math.round(
    twin.byType("ghat")
      .map((g) => ASSEMBLY_AREA_M2[g.sector] ?? 0)
      .reduce((a, area) => a + area / GHAT_AREA_PER_PERSON_M2 / GHAT_DWELL_MIN, 0),
  );
  const exitPerMin = liveState.sectors.reduce(
    (a, s) => a + (s.egressCapacityPerMin ?? 0), 0,
  );
  const returnPerMin = Math.round(busPerMin * 0.9 + railPerMin);

  return {
    rail:     { capacityPerMin: railPerMin,     assets: ["TRN-01"] },
    bus:      { capacityPerMin: busPerMin,      assets: busTerminals.map((b) => b.id) },
    parking:  { capacityPerMin: parkingPerMin,  assets: parking.map((p) => p.id) },
    corridor: { capacityPerMin: corridorPerMin, assets: corridors.map((c) => c.id) },
    ghat:     { capacityPerMin: ghatPerMin,     assets: twin.byType("ghat").map((g) => g.id) },
    exit:     { capacityPerMin: exitPerMin,     assets: twin.byType("gate").map((g) => g.id) },
    return:   { capacityPerMin: returnPerMin,   assets: busTerminals.map((b) => b.id) },
  };
}

/* ── The chain ─────────────────────────────────────────────────────────
 * `disruptions` is a map of stage → capacity multiplier (0 = fully held).
 * §6's transport-hold scenario sets one; by default there are none. */
export function assessMobilityChain(liveState, { disruptions = {}, twin = null } = {}) {
  const t = twin ?? buildTwin(liveState);
  const caps = stageCapacities(t, liveState);

  /* Demand entering the chain is what is actually arriving right now —
   * the theatre's current inflow — not any stage's capacity. Seeding the
   * chain with a capacity figure is what made rail look like a permanent
   * ceiling. */
  const headDemand = Math.round(
    liveState.sectors.reduce((a, s) => a + (s.inflowPerMin ?? 0), 0),
  );

  /* Rail, bus and parking are PARALLEL ingress channels, not a queue.
   * A pilgrim arriving by train does not then use a car park. Modelling
   * them serially (as this did) pushed the entire theatre's arrivals
   * through the smallest of the three and made parking — the channel
   * fewest people use — the binding constraint on everything.
   *
   * So demand splits across them by mode share, each is judged against
   * its own capacity, and what gets through converges on the corridor.
   * The corridor onwards genuinely is serial: everyone walks. */
  let carried = 0;
  let bindingCapacity = Infinity;
  let bindingStage = null;

  const stages = STAGE_ORDER.map((key) => {
    const base = caps[key].capacityPerMin;
    const multiplier = disruptions[key] ?? 1;
    const effective = Math.round(base * multiplier);
    const parallel = INGRESS_CHANNELS[key] != null;

    /* An ingress channel takes its mode share of arrivals; a serial stage
     * takes whatever the stage before it passed on. */
    const incoming = parallel
      ? headDemand * INGRESS_CHANNELS[key]
      : carried;

    /* Only serial stages can bind the whole chain. A saturated car park
     * constrains the people using it, not the ones arriving on foot — so
     * it is reported as saturated without being blamed for the theatre. */
    if (!parallel && effective < bindingCapacity) {
      bindingCapacity = effective;
      bindingStage = key;
    }

    const through = Math.min(incoming, effective);
    const queueRatePerMin = Math.max(0, incoming - effective);
    const utilisation = effective > 0 ? incoming / effective : null;

    const row = {
      stage: key,
      label: STAGE_META[key].label,
      agency: STAGE_META[key].agency,
      assets: caps[key].assets,
      lagFromHeadMin: CUMULATIVE_LAG_MIN[key],
      baseCapacityPerMin: base,
      effectiveCapacityPerMin: effective,
      disrupted: multiplier < 1,
      disruptionMultiplier: multiplier,
      parallel,
      demandPerMin: Math.round(incoming),
      throughputPerMin: Math.round(through),
      queueRatePerMin: Math.round(queueRatePerMin),
      /* Null rather than Infinity when a stage is fully held — an infinite
       * utilisation is not a number, it is a finding. */
      utilisation: utilisation == null ? null : Number(utilisation.toFixed(2)),
      state:
        effective === 0 ? "held"
        : utilisation > 1.2 ? "overloaded"
        : utilisation > 1 ? "saturated"
        : utilisation > 0.8 ? "tight" : "ok",
      trust: TRUST.SIMULATED,
    };

    /* Parallel channels ADD into what reaches the corridor; serial stages
     * replace it. */
    carried = parallel ? carried + through : through;
    return row;
  });

  const binding = stages.find((s) => s.stage === bindingStage) ?? null;

  return {
    stages,
    /* The single most useful output: which stage limits the whole chain,
     * and which department owns it. */
    binding,
    headDemandPerMin: headDemand,
    chainThroughputPerMin: Math.round(carried),
    lossFromHead: Math.round(headDemand - carried),
    propagation: propagate(stages),
    trust: TRUST.SIMULATED,
  };
}

/* ── Propagation ───────────────────────────────────────────────────────
 * The §11 payload. A queue forming at an upstream stage does not stay
 * there: it arrives downstream after the cumulative lag, as crowd pressure
 * in a sector that currently looks fine. */
function propagate(stages) {
  const events = [];

  stages.forEach((s, i) => {
    if (s.queueRatePerMin <= 0 && !s.disrupted) return;

    stages.slice(i + 1).forEach((downstreamStage) => {
      const lag = downstreamStage.lagFromHeadMin - s.lagFromHeadMin;
      if (lag <= 0) return;

      /* A stage that is HELD sends nothing downstream — the downstream
       * effect is a shortfall, not a surge. Distinguishing these two is the
       * difference between holding buses to relieve a ghat and holding
       * buses into a ghat that is about to empty. */
      const effect = s.effectiveCapacityPerMin === 0 ? "shortfall" : "surge";
      const magnitude = effect === "shortfall"
        ? s.demandPerMin
        : s.queueRatePerMin;
      if (magnitude <= 0) return;

      events.push({
        fromStage: s.stage,
        fromLabel: s.label,
        toStage: downstreamStage.stage,
        toLabel: downstreamStage.label,
        effect,
        inMinutes: lag,
        peoplePerMin: Math.round(magnitude),
        /* Over the lag window, this is how many people the downstream stage
         * will see arrive that it is not currently planning for. */
        cumulativePeople: Math.round(magnitude * lag),
        agency: STAGE_META[downstreamStage.stage].agency,
        narrative:
          effect === "shortfall"
            ? `${s.label.en} is held, so ${downstreamStage.label.en} will see roughly ${Math.round(magnitude)} fewer people per minute starting in ${lag} minutes.`
            : `${s.label.en} is queuing at ${Math.round(magnitude)} people/min; that pressure reaches ${downstreamStage.label.en} in ${lag} minutes, about ${Math.round(magnitude * lag).toLocaleString("en-IN")} people.`,
      });
    });
  });

  /* Nearest effects first — an operator acts on what lands soonest. */
  return events.sort((a, b) => a.inMinutes - b.inMinutes);
}

export const MOBILITY_ASSUMPTIONS = [
  `Stage capacities are planning assumptions: station throughput ${STATION_THROUGHPUT_PER_MIN}/min, corridor capacity from width at ~1.3 persons/m/s, exit capacity from the same figure the live model uses — so the chain and the map cannot disagree.`,
  "Capacity and demand are kept strictly apart. Demand entering the chain is the theatre's current inflow; a stage's capacity is what it can process. Conflating them makes a daily average look like a hard ceiling.",
  `Transfer lags between stages are fixed (rail→bus ${STAGE_LAG_MIN.rail_to_bus} min, corridor→ghat ${STAGE_LAG_MIN.corridor_to_ghat} min). Real lags vary with crowd density and would be measured.`,
  "Propagation is linear and does not model people giving up, rerouting or turning back. It overstates downstream pressure for long lags.",
  "A held stage produces a downstream SHORTFALL, not a surge. Treating every disruption as a surge would recommend holding transport into a ghat that is already emptying.",
  "The binding constraint is the smallest effective capacity among the SERIAL stages upstream — which is why the stage reporting the symptom is rarely the stage that must act.",
  `Rail, bus and parking are parallel ingress channels carrying ${Math.round(INGRESS_CHANNELS.rail * 100)}%, ${Math.round(INGRESS_CHANNELS.bus * 100)}% and ${Math.round(INGRESS_CHANNELS.parking * 100)}% of arrivals. They are not a queue: a pilgrim arriving by train does not then use a car park. A saturated channel is reported without being blamed for constraining people who never use it.`,
];
